import React, { useEffect, useRef, useState } from "react";
import { Search,RefreshCw} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import {
  addTab,
  removeTab,
  setActiveTab,
  setStatusFilter,
  setListViewState,
  setDefaultTabs,
} from "../../../redux/slices/shipmentSlice";
import ShipmentDetailsView from "./TabContent/ViewShipment/ShipmentDetailsView";
import { countActiveFilters, DEFAULT_ADVANCED_FILTERS } from "./TabContent/shipmentShared";
import { FiSearch } from "react-icons/fi";

// UI & utility imports
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";


// Our tab components
import ShipmentsTable from "./TabContent/ShipmentTable";
import DropTrailers from "./TabContent/DropTrailers";
import CreateShipmentTab from "./TabContent/CreateShipmentTab";
import ShipmentSearchSubTabs from "./TabContent/SearchShipment/ShipmentSearchSubTabs";
import axios from "axios";
import Cookies from "js-cookie";


const DEFAULT_STATUS_FILTER = "New";

const ShipmentDetails = () => {
  const dispatch = useDispatch();
  const { tabs, activeTab, defaultTabsCount } = useSelector(selectModuleTabs("shipments"));
  const statusFilterFromStore = useSelector((state) => state.shipments.statusFilter);
  const statusFilter =
    statusFilterFromStore === undefined ? DEFAULT_STATUS_FILTER : statusFilterFromStore;
  // alias to avoid any stale references during hot reloads
  const filter = statusFilter;
  const [totalShipments, setTotalShipments] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(false); //Force fetch when search button is clicked
  const [reloading, setReloading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusMapView, setStatusMapView] = useState({});
  const carrierFilterInitialized = useRef(false);
  const initialStatusFilter = useRef(statusFilterFromStore);
  const listViewState = useSelector((s) => s.shipments.listViewState);
  const prevTabStateKeyRef = useRef(null);

  const currentTabStateKey = tabs[activeTab]?.id ?? "shipment-list";

  // Restore per-tab quick filter only when switching tabs — not on initial mount,
  // where each tab's saved view owns filter via onApplyConfig.
  useEffect(() => {
    const prevKey = prevTabStateKeyRef.current;
    prevTabStateKeyRef.current = currentTabStateKey;

    if (prevKey === null || prevKey === currentTabStateKey) return;

    const tabState = listViewState[currentTabStateKey];
    if (!tabState) return;

    if (tabState.filter !== undefined && tabState.filter !== null) {
      dispatch(setStatusFilter(tabState.filter));
    } else if ("filter" in tabState) {
      dispatch(setStatusFilter(null));
    }
  }, [currentTabStateKey, listViewState, dispatch]);

  const currentTabAdvancedFilters = useSelector(
    (s) => s.shipments.advancedFilters?.[currentTabStateKey] ?? DEFAULT_ADVANCED_FILTERS
  );
  const advancedFiltersActive = countActiveFilters(currentTabAdvancedFilters) > 0;


  const statusApiToView = {
    Completed: "Delivered",
    Active: "In-Transit",
    Rejected: null,
  };

  const statusViewToApi = Object.fromEntries(
    Object.entries(statusApiToView).map(([api, view]) => [view, api])
  );

  const toViewStatus = (apiStatus) => statusApiToView[apiStatus] ?? apiStatus;
  const isHiddenStatus = (apiStatus) => statusApiToView[apiStatus] === null;
  const toApiStatus  = (viewStatus) => statusViewToApi[viewStatus] ?? viewStatus;

  const filterButtonValue = advancedFiltersActive
    ? "All Statuses"
    : (statusFilter ? toViewStatus(statusFilter) : "All Statuses");


  // ✅ Log Redux tabs for debugging
  useEffect(() => {
  }, [tabs]);

  // ✅ Dynamically set default tabs based on org type (no reload needed)
  useEffect(() => {
    try {
      const stored = localStorage.getItem("organizationTypes");
      const parsed = stored ? JSON.parse(stored) : [];
      const normalized = (Array.isArray(parsed) ? parsed : [])
        .map((t) => String(t ?? "").toLowerCase().trim())
        .filter(Boolean);
      const isOnlyShipper =
        normalized.length === 1 && normalized[0] === "shipper";
      const isOnlyCarrier =
        normalized.length === 1 && normalized[0] === "carrier";

      const baseTabs = [{ id: "shipment-list", name: "Shipment List" }];
      const shipperTabs = [
        ...baseTabs,
        { id: "drop-trailers", name: "Drop Trailers" },
      ];

      dispatch(setDefaultTabs(isOnlyShipper || isOnlyCarrier ? shipperTabs : baseTabs));

        // For carriers, default to "All Statuses" only when they still have the initial default filter
        if (
            isOnlyCarrier &&
            !carrierFilterInitialized.current &&
            (initialStatusFilter.current === DEFAULT_STATUS_FILTER || initialStatusFilter.current === undefined)
        ) {
            carrierFilterInitialized.current = true;
            dispatch(setStatusFilter(null));
            dispatch(setListViewState({ stateKey: currentTabStateKey, filter: null }));
        }
    } catch (err) {
        console.error("Failed to set tabs from organizationTypes", err);
    }
  }, [dispatch]);

   // ✅ Handle filter change
  //  const handleFilter = (selectedValue) => {
  //   setFilter(selectedValue === "Status" ? null : selectedValue);
  //   setSearchQuery(filter != "Status" ? filter : null); // Clear search when filter changes
  //   handleSearch() // Force fetch with new filter
  // };

  const handleFilter = (selectedViewValue) => {
    const nextApiValue =
      selectedViewValue === "All Statuses" ? null : toApiStatus(selectedViewValue);

    dispatch(setStatusFilter(nextApiValue));            // store API value (Completed/Active)
    dispatch(setListViewState({ stateKey: currentTabStateKey, filter: nextApiValue }));
    setSearchQuery("");                 // clear the free-text search
    setSearchTrigger(prev => !prev);    // force refetch using filter
  };


   // ✅ Clear filters
   const handleClearFilters = () => {
    setSearchQuery("");
    dispatch(setStatusFilter(DEFAULT_STATUS_FILTER));
    dispatch(setListViewState({ stateKey: currentTabStateKey, filter: DEFAULT_STATUS_FILTER }));
    setSearchTrigger((prev) => !prev); // ✅ Force fetch with cleared filters
  };


  useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/shipments/statuses`,
        {
          headers: { Authorization: `Bearer ${Cookies.get("authToken")}` },
        }
      );

      if (cancelled) return;

       const apiStatuses = (Array.isArray(res.data) ? res.data : [])
        .map(s => String(s).trim())
        .filter(Boolean);

      const viewStatuses = Array.from(
        new Set(
          apiStatuses
            .filter(s => !isHiddenStatus(s))
            .map(toViewStatus)
            .filter(Boolean)
        )
      );

setStatusOptions(viewStatuses);

      setStatusOptions(viewStatuses);
    } catch (err) {
      console.error("Failed to load shipment statuses:", err);

      const fallbackApi = [
        "New","Assigned","Unassigned","Tendered","Accepted",
        "Rejected","Completed","Active","Cancelled"
      ];
      
      const viewStatuses = Array.from(
          new Set(
            fallbackApi
              .filter(s => !isHiddenStatus(s))
              .map(toViewStatus)
              .filter(Boolean)
          )
        );

      setStatusOptions(viewStatuses);
    }
  })();

  return () => { cancelled = true; };
}, []);


  // Default content for each tab
  const defaultTabContents = {
    "shipment-list": (
      <ShipmentsTable
        key="shipment-list"
        setTotalShipments={setTotalShipments}
        filter={statusFilter}
        searchTrigger={searchTrigger}
        searchQuery=""
        stateKey="shipment-list"
      />
    ),
    "drop-trailers": (
      <DropTrailers
        key="drop-trailers"
        setTotalShipments={setTotalShipments}
        filter={statusFilter}
        searchTrigger={searchTrigger}
        searchQuery=""
      />
    ),
    // "shipment-form": <ShipmentForm key="shipment-form" />,
  };

  // When clicking "Create Shipment", dispatch a new tab
  const handleCreateShipment = () => {
    dispatch(addTab());
  };

  // Remove tab only if it's dynamic
  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ tabIndex: index }));
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const activeDef = tabs[activeTab];
    const baseId =
      activeDef && (activeDef.id === "drop-trailers" || activeDef.id === "shipment-list" || activeDef.id === "missing-appt")
        ? activeDef.id
        : "shipment-list";

    const newTabId = `search-${baseId}-${Date.now()}`;
    dispatch(addTab({
      id: newTabId,
      name: `Search(${baseId === "drop-trailers" ? "Drop" : "Shipments"}): ${searchQuery}`,
      contentType: "searchResults",
      baseId,
      searchQuery,
      searchTrigger,          // ← snapshot the value right now
      isDynamic: true,
      createdAt: Date.now(),
    }));
    dispatch(setActiveTab({ index: tabs.length }));
  };



  // const handleSearch = () => {
  //   if (!searchQuery.trim()) return;

  //   // Create a unique id for the search tab and provide custom name/content.
  //   const newTabId = `search-${Date.now()}`;
  //   dispatch(addTab({
  //     id: newTabId,
  //     name: `Search: ${searchQuery}`,
  //     contentType: "searchResults",
  //     searchQuery,
  //     isDynamic: true,
  //     createdAt: Date.now(),
  //   }));
  //   dispatch(setActiveTab({ index: tabs.length })); // Activate new tab
  // };

  return (
    <div className="p-fluid-1 shadow-md flex flex-col font-roboto flex-1 h-full min-h-0 overflow-hidden">
    {/* Top Action/Filter Bar */}
    <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
      {/* Left side: Filter buttons, search box */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterButton
          text={filterButtonValue}
          options={["All Statuses", ...statusOptions]}
          selectedValue={filterButtonValue}
          onSelect={handleFilter}
          backgroundColor="white"
          textColor="black"
        />
  
        <SearchBox
          value={searchQuery}
          onChange={setSearchQuery}
          onSearch={handleSearch}
          placeholder="Search Shipments"
        />

        <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
          {totalShipments} Shipments
        </span>
        <button
          onClick={() => {
            setReloading(true);           // spin the icon briefly
            setTimeout(() => window.location.reload(), 150); // tiny delay for visual feedback
          }}
          className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
          title="Reload page"
          aria-label="Reload page"
        >
          <RefreshCw
            size={16}
            className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${reloading ? "animate-spin" : "hover:text-gray-700"}`}
          />
        </button>
      </div>
  
      {/* Right side: Download button & Create Shipment */}
      <div className="flex items-center gap-2">
        {/* <div className="p-2 rounded-md hover:bg-gray-100 transition cursor-pointer">
          <Download size={24} strokeWidth={2} className="text-[#223688]" />
        </div> */}
  
        <Button
          text="Create Shipment"
          onClick={handleCreateShipment}
          backgroundColor="#223688"
          textColor="white"
          fontSize="14px"
          padding="10px 18px"
          borderRadius="24px"
        />
      </div>
    </div>
  
    {/* Tab Content Area */}
  <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
      <Tabs
        tabs={tabs.map(t => t.name)}
        tabContents={tabs.map((tab) => {
          if (tab.contentType === "searchResults") {
            if (tab.baseId === "drop-trailers") {
              return (
                <DropTrailers
                  key={tab.id}
                  setTotalShipments={setTotalShipments}
                  filter={statusFilter}
                  searchTrigger={tab.searchTrigger} 
                  searchQuery={tab.searchQuery}   // <- use the tab’s query
                  stateKey={tab.id}
                />
              );
            }
            // default: shipments search
            return (
                <ShipmentSearchSubTabs
                  key={tab.id}
                  setTotalShipments={setTotalShipments}
                  searchTrigger={tab.searchTrigger} 
                  filter={statusFilter}  
                  searchQuery={tab.searchQuery}     // <- use the tab’s query
                  stateKey={tab.id}
                />
              );
          }

          if (tab.contentType === "shipmentView") {
            return (
              <ShipmentDetailsView
                key={tab.shipmentNumber}
                shipmentNumber={tab.shipmentNumber}
                shipmentOfficeCode={tab.shipmentOfficeCode}
              />
            );
          }

          return (
            defaultTabContents[tab.id] || (
              <CreateShipmentTab
                key={tab.id}
                title={tab.name}
                setActiveTab={(payload) => dispatch(setActiveTab(payload))}
                tabs={tabs}
              />
            )
          );
        })}
        activeTab={activeTab}
        onTabChange={(index) => dispatch(setActiveTab({ index }))}
        isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
        onRemoveTab={handleRemoveTab}
        keepAlive={tabs.map(
          (tab) => tab.contentType === "searchResults" || tab.id === "shipment-list"
        )}
      />
    </div>
  </div>
  
  );
};

export default ShipmentDetails;
